import Promise from 'bluebird';
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose, noop, isUndefined } from 'underscore';
import { QueryParam, withScope } from '@bingads-webui-react/scope-service';
import { withSiteMap } from '@bingads-webui-campaign-react/with-site-map';
import { withCcJsData, CcJsDataContextProvider } from '@bingads-webui-cc-react/js-data-rest';
import { Scenario, Activity, withActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { ImportCalloutCard as ImportCalloutCardInternal } from './src/index';
import {
  gotoPageByClickingRecommendation,
  adoptResumeImportRecommendation as adoptResumeImportRecommendationImpl,
  adoptResumeScheduleRecommendation as adoptResumeScheduleRecommendationImpl,
} from './src/default-import-recommendation-service';

const ImportCalloutCardWithNavigation = ({
  scope, siteMap, serverContext, onNext, shouldDisplayDelay, ...rest
}) => {
  const permissions = {
    ...serverContext.Permissions,
    dynamic: serverContext.DynamicPermissions || {},
  };
  //
  const gotoPage = useCallback((page, recommendationId) => {
    gotoPageByClickingRecommendation(scope, siteMap, page, recommendationId);
  }, [scope, siteMap]);

  // skip for readonly users
  if (!permissions || permissions.IsReadOnlyUser
    || !serverContext || serverContext.CurrentAccountInActive) {
    if (!isUndefined(onNext)) {
      onNext();
    }
    return <></>;
  }

  // only show it in account level
  if (!siteMap || !scope || !scope.values || !scope.values.aid) {
    if (!isUndefined(onNext)) {
      onNext();
    }
    return <></>;
  }

  return (
    <ImportCalloutCardInternal
      gotoPage={gotoPage}
      serverContext={serverContext}
      aid={scope.values.aid}
      onNext={onNext}
      shouldDisplayDelay={shouldDisplayDelay}
      {...rest}
    />
  );
};

ImportCalloutCardWithNavigation.propTypes = {
  serverContext: PropTypes.shape({}).isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })).isRequired,
  onNext: PropTypes.func,
  shouldDisplayDelay: PropTypes.bool,
};

ImportCalloutCardWithNavigation.defaultProps = {
  onNext: noop,
  shouldDisplayDelay: true,
};

const wrapper = compose(
  withI18nConsumer,
  withCcJsData,
  withActivityContext,
  withSiteMap,
  withScope,
  withPermissions
);

const ImportCalloutCardWithHOC = wrapper(ImportCalloutCardWithNavigation);

export const ImportCalloutCard = ({ serverContext, onNext, shouldDisplayDelay }) => (
  <Scenario name="ImportCalloutCard">
    <Activity name="ShowImportCalloutCard">
      <CcJsDataContextProvider
        userId={serverContext.CurrentUser.Id}
        customerId={serverContext.CurrentCustomer.Id}
        clientCenterBaseUrl={serverContext.ServicesEndpoints.ClientCenterUIAPI}
      >
        <ImportCalloutCardWithHOC serverContext={serverContext} onNext={onNext} shouldDisplayDelay={shouldDisplayDelay} />
      </CcJsDataContextProvider>
    </Activity>
  </Scenario>
);

ImportCalloutCard.propTypes = {
  serverContext: PropTypes.shape({}).isRequired,
  onNext: PropTypes.func,
  shouldDisplayDelay: PropTypes.bool,
};

ImportCalloutCard.defaultProps = {
  onNext: noop,
  shouldDisplayDelay: true,
};

// since there already has a ccjs data provider in ui-next, we don't need to create another one. this will avoid duplicate call.
export const ImportCalloutCardWithoutCCJsData = ({ serverContext, onNext, shouldDisplayDelay }) => (
  <Scenario name="ImportCalloutCard">
    <Activity name="ShowImportCalloutCard">
      <ImportCalloutCardWithHOC serverContext={serverContext} onNext={onNext} shouldDisplayDelay={shouldDisplayDelay} />
    </Activity>
  </Scenario>
);

ImportCalloutCardWithoutCCJsData.propTypes = {
  serverContext: PropTypes.shape({}).isRequired,
  onNext: PropTypes.func,
  shouldDisplayDelay: PropTypes.bool,
};

ImportCalloutCardWithoutCCJsData.defaultProps = {
  onNext: noop,
  shouldDisplayDelay: true,
};

export const adoptResumeScheduleRecommendation = ({
  scope, ...rest
}) => {
  // support account level only
  if (!scope || !scope.values || !scope.values.aid) {
    return Promise.resolve(null);
  }

  return adoptResumeScheduleRecommendationImpl({ ...rest, scope });
};

export const adoptResumeImportRecommendation = ({
  scope, ...rest
}) => {
  // support account level only
  if (!scope || !scope.values || !scope.values.aid) {
    return Promise.resolve(null);
  }

  return adoptResumeImportRecommendationImpl({ ...rest, scope });
};

